import {
  flow,
  identity,
  pickBy,
  isEmpty,
  toPairs,
  map,
  join,
  startCase,
  get
} from 'lodash/fp';
import React from 'react';
import PropTypes from 'prop-types'

import modalFoundation from '../../hoc/modalFoundation.js';
import ticketUpdateById from '../../hoc/ticketUpdateByIdHoc.js';

import './ticketChangeModal.scss';

/**
 * Defines the id of the modal.
 * @type {string}
 */
const MODAL_ID = 'ticket-change-modal';

/**
 * Modal Options.
 * @type {{closeOnClick: boolean}}
 */
const modalOptions = {
  closeOnClick: true,
};

/**
 * The ticket change form modal.
 */
export class TicketChangeModal extends React.Component {
  /**
   * @constructor
   * @param {{}} props
   * @param {{}} componentContext
   */
  constructor(props, componentContext) {
    super(props, componentContext);

    this.state = {
      errorEmptyForm: false,
      form: {
        schedule: '',
        location: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        comments: '',
      },
    }
  }

  /**
   * Triggered when the component is about to receive new props.
   *
   * @param {{}} nextProps
   */
  componentWillReceiveProps(nextProps) {
    if (this.props.ticketUpdateById.isLoading && !nextProps.ticketUpdateById.isLoading) {
      if (!nextProps.ticketUpdateById.error) {
        this.closeModal(true);
      }
    }
  }

  /**
   * Updates the state when the form changes.
   *
   * @param {string} field
   * @returns {function}
   */
  updateForm = (field) => {
    /**
     * Updates the state with the given form value.
     *
     * @param {string|{target: {value: string}}} formValueOrEvent
     */
    return (formValueOrEvent) => {
      let formValue = formValueOrEvent;
      if (typeof formValueOrEvent === 'object' && formValueOrEvent.target) {
        formValue = formValueOrEvent.target.value;
      }

      const newForm = Object.assign({}, this.state.form, {
        [field]: formValue
      });

      this.setState({
        form: newForm,
      });
    };
  };

  /**
   * Closes the modal.
   *
   * @param {boolean} afterChanges
   */
  closeModal = (afterChanges) => {
    this.setState({
      form: {
        schedule: '',
        location: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        comments: '',
      }
    }, () => {
      this.props.modalFoundation.hide();

      if (this.props.onClose) {
        this.props.onClose(afterChanges || false);
      }
    });
  };

  /**
   * Submits the change request form.
   */
  submitForm = () => {
    this.setState({
      errorEmptyForm: false,
    }, () => {
      const formData = pickBy(identity, this.state.form);

      if (isEmpty(formData)) {
        this.setState({
          errorEmptyForm: true
        });
        return;
      }

      const changeRequest = flow(
        toPairs,
        map(([key, value]) => `${startCase(key)}: ${value}`),
        join('\n')
      )(formData);

      if (this.props.ticketId) {
        this.props.ticketUpdateById.updateTicket(this.props.ticketId, changeRequest);
      }
    });
  };

  /**
   * Renders the modal.
   *
   * @returns {object}
   */
  render() {
    const isSaving = this.props.ticketUpdateById.isLoading || false;
    const saveError = this.props.ticketUpdateById.error || null;
    const saveErrorMessage = get('response.data.message', saveError);
    return (
      <div id={MODAL_ID} className="reveal wgl-modal" tabIndex="-1" role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Please list the information that needs to be corrected</h2>
            </div>
            <div className="modal-body">
              <form className="form">
                <div className="row">
                  <div className="small-12 columns">
                    <label>
                      Location
                      <input
                        type="text"
                        placeholder="Address, City, State, Zip Code"
                        value={this.state.form.location}
                        onChange={this.updateForm('location')}
                      />
                    </label>
                  </div>
                </div>
                <div className="row">
                  <div className="small-12 medium-4 columns">
                    <label>
                      Your Name
                      <input
                        type="datetime"
                        placeholder="Name"
                        value={this.state.form.contactName}
                        onChange={this.updateForm('contactName')}
                      />
                    </label>
                  </div>
                  <div className="small-12 medium-4 columns">
                    <label>
                      Your Phone
                      <input
                        type="text"
                        placeholder="Phone"
                        value={this.state.form.contactPhone}
                        onChange={this.updateForm('contactPhone')}
                      />
                    </label>
                  </div>
                  <div className="small-12 medium-4 columns">
                    <label>
                      Your Email
                      <input
                        type="text"
                        placeholder="Email"
                        value={this.state.form.contactEmail}
                        onChange={this.updateForm('contactEmail')}
                      />
                    </label>
                  </div>
                </div>
                <div className="row">
                  <div className="small-12 columns">
                    <label>
                      Additional Comments
                      <textarea
                        placeholder="None"
                        value={this.state.form.comments}
                        onChange={this.updateForm('comments')}
                      />
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-footer">
              {(this.state.errorEmptyForm) && (
                <div className="callout alert">
                  You must fill out at least one field before submitting.
                </div>
              )}

              {(isSaving) && (
                <div className="callout primary">
                  Submitting Change Request...
                </div>
              )}

              {(saveError) && (
                <div className="callout alert">
                  <p>An error occurred and we were not able to save your change request:</p>
                  <p className="error-message">{saveErrorMessage && saveErrorMessage}</p>
                  <p>
                    <span className="inline-text">Please contact WeGoLook at</span>
                    <span className="inline-text"><a href="mailto:support@wegolook.com">WeGoLook Support</a></span>
                    <span className="inline-text">or call +1 (855) 947-1788.</span>
                  </p>
                </div>
              )}

              <div className="button-group large expanded">
                <button
                  type="button"
                  className="button secondary"
                  onClick={this.closeModal.bind(this, false)}
                >Cancel</button>

                <button
                  type="button"
                  className="button"
                  onClick={this.submitForm}
                >Submit</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

TicketChangeModal.propTypes = {
  onClose: PropTypes.func,
  ticketId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,

  // ModalFoundation HOC
  modalFoundation: PropTypes.shape({
    hide: PropTypes.func.isRequired,
  }),

  // TicketUpdateById HOC
  ticketUpdateById: PropTypes.shape({
    updateTicket: PropTypes.func,
    isLoading: PropTypes.bool,
    success: PropTypes.bool,
    error: PropTypes.object,
  }),
};

export default modalFoundation(MODAL_ID, modalOptions)(
  ticketUpdateById(
    TicketChangeModal
  )
);



// WEBPACK FOOTER //
// ./src/components/modals/ticketChange/TicketChangeModal.js